// App.jsx - Updated with new theme and structure
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Stack,
  Alert,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  History,
  AccessTime,
  MailOutline,
  Visibility,
  Forum,
  AccountCircle,
} from '@mui/icons-material';
import Sidebar from './components/Sidebar/Sidebar';
import SearchDashboard from './components/SearchDashboard/SearchDashboard';
import { useCarrierData } from './hooks/useCarrierData';
import { useAuth } from './context/AuthContext';
import AdminPortal from './components/AdminPortal/AdminPortalProfessional';
import EmailWorkspace from './components/Email/EmailWorkspace';
import LandingPage from './components/LandingPage/LandingPage';
import BlogPage from './components/Blog/BlogPage';
import LimitReachedDialog from './components/Common/LimitReachedDialog';
import UserProfile from './components/UserProfile/UserProfile';
import { adminApi } from './services/adminApi';
import { useToast } from './contexts/ToastContext';
import './styles/globals.css';

const DEFAULT_PROFILE_SETTINGS = {
  defaultHomeTab: 'search',
  autoReturnToSearchAfterQuickEmail: true,
  compactAccounts: true,
  inboxAutoRefresh: true,
  showExpiryAlerts: true,
};

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light theme
          primary: {
            main: '#2563eb',
            light: '#3b82f6',
            dark: '#1d4ed8'
          },
          secondary: {
            main: '#059669',
            light: '#10b981',
            dark: '#047857'
          },
          background: {
            default: '#f8fafc',
            paper: '#ffffff',
          },
          text: {
            primary: '#1e293b',
            secondary: '#64748b',
          },
          grey: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
          }
        }
      : {
          // Dark theme
          primary: {
            main: '#3b82f6',
            light: '#60a5fa',
            dark: '#2563eb'
          },
          secondary: {
            main: '#10b981',
            light: '#34d399',
            dark: '#059669'
          },
          background: {
            default: '#0f172a',
            paper: '#1e293b',
          },
          text: {
            primary: '#f1f5f9',
            secondary: '#cbd5e1',
          },
          grey: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
          }
        }),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid',
          borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

const drawerWidth = 280;

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [quickComposePayload, setQuickComposePayload] = useState(null);
  const [quickComposeReturnToSearch, setQuickComposeReturnToSearch] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitType, setLimitType] = useState('');
  const [limitData, setLimitData] = useState(null);
  const [landingBatchLimitOpen, setLandingBatchLimitOpen] = useState(false);
  const { isAuthenticated, user, logout, token, isLoading, refreshSession } = useAuth();
  const toast = useToast();
  const isAdminUser = user?.role === 'admin';
  const workTickRef = React.useRef(null);
  const defaultTabAppliedRef = React.useRef(false);
  
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  useEffect(() => {
    setDarkMode(prefersDarkMode);
  }, [prefersDarkMode]);

  useEffect(() => {
    if (isAuthenticated) {
      setShowLandingPage(false);
    } else {
      // Check if user was forcefully logged out
      if (typeof window !== 'undefined') {
        const logoutReason = window.localStorage.getItem('logout_reason');
        if (logoutReason === 'forced') {
          toast.warning('Your session has been ended. You have been logged in from another device.', {
            title: 'Session Ended',
            duration: 8000,
          });
          window.localStorage.removeItem('logout_reason');
        }
      }
      setShowLandingPage(true);
    }
  }, [isAuthenticated, toast]);

  useEffect(() => {
    if (activeTab === 'admin' && !isAdminUser) {
      setActiveTab('search');
    }
  }, [activeTab, isAdminUser]);

  useEffect(() => {
    let active = true;

    if (!isAuthenticated || !token) {
      setProfileSettings(DEFAULT_PROFILE_SETTINGS);
      defaultTabAppliedRef.current = false;
      return undefined;
    }

    adminApi.loadPrefs(token)
      .then((res) => {
        if (!active) return;
        const prefs = res?.prefs || {};
        const nextSettings = {
          ...DEFAULT_PROFILE_SETTINGS,
          ...(prefs?.profileSettings || {}),
          compactAccounts: typeof prefs?.compactAccounts === 'boolean'
            ? prefs.compactAccounts
            : DEFAULT_PROFILE_SETTINGS.compactAccounts,
        };
        setProfileSettings(nextSettings);

        if (!defaultTabAppliedRef.current && nextSettings.defaultHomeTab && activeTab === 'search') {
          setActiveTab(nextSettings.defaultHomeTab);
          defaultTabAppliedRef.current = true;
        }
      })
      .catch(() => {
        if (!active) return;
        setProfileSettings(DEFAULT_PROFILE_SETTINGS);
      });

    return () => {
      active = false;
    };
  }, [activeTab, isAuthenticated, token]);

  const handleNavigateToApp = () => {
    setShowLandingPage(false);
  };

  const handleLogoutFromApp = () => {
    setShowLandingPage(true);
  };

  const theme = React.useMemo(() => 
    createTheme(getDesignTokens(darkMode ? 'dark' : 'light')),
    [darkMode]
  );

  const [activityStats, setActivityStats] = useState(null);
  const [profileSettings, setProfileSettings] = useState(DEFAULT_PROFILE_SETTINGS);
  const [isBlogRoute, setIsBlogRoute] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.replace(/\/+$/, '') === '/blog';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncRoute = () => {
      setIsBlogRoute(window.location.pathname.replace(/\/+$/, '') === '/blog');
    };
    syncRoute();
    window.addEventListener('popstate', syncRoute);
    return () => window.removeEventListener('popstate', syncRoute);
  }, []);

  useEffect(() => {
    let timer;
    const load = () => {
      if (!isAuthenticated || !token) {
        setActivityStats(null);
        return;
      }
      adminApi.getActivityStats(token)
        .then((res) => setActivityStats(res?.stats || null))
        .catch(() => {});
    };
    load();
    if (isAuthenticated && token) {
      timer = setInterval(load, 20000); // refresh every 20s
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isAuthenticated, token]);

  // Log periodic work ticks to estimate hours
  useEffect(() => {
    if (!isAuthenticated || !token) {
      if (workTickRef.current) clearInterval(workTickRef.current);
      return;
    }
    const sendTick = () => {
      adminApi.logActivity({ type: 'work_tick', source: 'app', metadata: { minutes: 5 } }, token).catch(() => {});
    };
    sendTick();
    workTickRef.current = setInterval(sendTick, 5 * 60 * 1000);
    return () => {
      if (workTickRef.current) clearInterval(workTickRef.current);
    };
  }, [isAuthenticated, token]);

  // Activity metrics from API fallback to local history
  const todayKey = new Date().toDateString();
  const thisMonthKey = `${new Date().getFullYear()}-${new Date().getMonth()}`;
  const searchesToday = activityStats ? activityStats.carrier_search?.today || 0 : searchHistory.filter((s) => new Date(s.timestamp).toDateString() === todayKey).length;
  const searchesMonth = activityStats ? activityStats.carrier_search?.month || 0 : searchHistory.filter((s) => {
    const d = new Date(s.timestamp);
    return `${d.getFullYear()}-${d.getMonth()}` === thisMonthKey;
  }).length;
  const callsToday = activityStats ? activityStats.call?.today || 0 : 0;
  const callsMonth = activityStats ? activityStats.call?.month || 0 : 0;
  const emailSentToday = activityStats ? activityStats.email_send?.today || 0 : 0;
  const emailSentMonth = activityStats ? activityStats.email_send?.month || 0 : 0;
  const inboxRepliesToday = activityStats ? activityStats.email_reply?.today || 0 : 0;
  const inboxRepliesMonth = activityStats ? activityStats.email_reply?.month || 0 : 0;
  const hoursWorkedToday = activityStats ? (activityStats.work_minutes?.today || 0) / 60 : 0;
  const hoursWorkedMonth = activityStats ? (activityStats.work_minutes?.month || 0) / 60 : 0;
  const formatHours = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0.0';
    return num.toFixed(1);
  };

  const {
    carrierData,
    loading,
    error,
    searchCarrier,
    batchSearch,
    clearBatchResults,
    showBasicInfo,
    toggleInfoView,
    usageRemaining,
    isBatchPaused,
    isBatchSearching,
    pauseBatchSearch,
    resumeBatchSearch,
  } = useCarrierData({ allowGuestSearch: true });
  
  // Monitor search errors and show limit dialog
  useEffect(() => {
    if (!error) return;
    
    console.log('Error detected in App:', error);
    
    // Check if it's a limit error
    if (typeof error === 'object') {
      if (error.status === 429 || error.limit_type || error.error?.includes('limit')) {
        const limitType = error.limit_type || 'search';
        console.log('Opening limit dialog for:', limitType, error);
        setLimitType(limitType);
        setLimitData(error);
        setLimitDialogOpen(true);
        return;
      }
    }
    
    // For string errors, check for limit keywords
    if (typeof error === 'string' && (error.toLowerCase().includes('limit'))) {
      toast.error(error);
    }
  }, [error, toast]);
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isEmailWorkspaceTab = activeTab === 'email' || activeTab === 'inbox' || 
    activeTab === 'email-compose' || activeTab === 'email-campaign' || 
    activeTab === 'email-templates' || activeTab === 'email-inbox' || 
    activeTab === 'email-seen' || activeTab === 'email-accounts';
  const isInboxOnlyView = activeTab === 'inbox';
  const [emailWorkspaceMounted, setEmailWorkspaceMounted] = useState(() => isAuthenticated && isEmailWorkspaceTab);

  useEffect(() => {
    if (isAuthenticated && isEmailWorkspaceTab) {
      setEmailWorkspaceMounted(true);
    }
  }, [isAuthenticated, isEmailWorkspaceTab]);

  useEffect(() => {
    if (!isAuthenticated) {
      setEmailWorkspaceMounted(false);
    }
  }, [isAuthenticated]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (term, type, filters = {}) => {
    const defaultFilters = {
      skipBrokers: false,
      skipInactive: false,
      showInactiveOnly: false,
      activeDuration: 0,
      startNumber: '',
      endNumber: '',
      resultLimit: 10
    };
    
    const finalFilters = { ...defaultFilters, ...filters };
    
    searchCarrier(term, type, finalFilters);
    
    const newSearch = {
      term,
      type,
      timestamp: new Date().toLocaleString(),
      result: carrierData ? 'Found' : 'Not Found',
      filters: finalFilters
    };
    
    setSearchHistory(prev => [newSearch, ...prev.slice(0, 9)]);
  };

  const handleBatchSearch = (batchInput, filters = {}) => {
    const searchTerms = batchInput.split('\n').filter(term => term.trim());
    const parseRange = (value) => {
      const match = value.match(/^\s*(\d+)\s*-\s*(\d+)\s*$/);
      if (!match) return null;
      const start = Number(match[1]);
      const end = Number(match[2]);
      if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
      return { start, end };
    };
    const totalRequested = searchTerms.reduce((sum, term) => {
      const range = parseRange(term);
      return sum + (range ? (range.end - range.start + 1) : 1);
    }, 0);
    
    if (showLandingPage && !isAuthenticated && totalRequested > 100) {
      setLandingBatchLimitOpen(true);
      return;
    }
    
    if (searchTerms.length > 0) {
      batchSearch(batchInput, filters);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const landingSearchProps = {
    onSearch: handleSearch,
    onBatchSearch: handleBatchSearch,
    clearBatchResults,
    onPauseBatchSearch: pauseBatchSearch,
    onResumeBatchSearch: resumeBatchSearch,
    loading,
    error,
    carrierData,
    showBasicInfo,
    onToggleView: toggleInfoView,
    isBatchPaused,
    isBatchSearching,
    autoHideOnSearch: false,
    showExportActions: false,
    onQuickEmail: () => {
      toast.info('Quick email compose is available after login.');
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Show Landing Page if user not authenticated */}
      {showLandingPage ? (
        isBlogRoute ? (
          <BlogPage toggleDarkMode={toggleDarkMode} />
        ) : (
          <LandingPage 
            onNavigateToApp={handleNavigateToApp}
            currentUser={user}
            toggleDarkMode={toggleDarkMode}
            landingSearchProps={landingSearchProps}
          />
        )
      ) : (
        // Show App Dashboard
        <Box sx={{ display: 'flex' }}>
          {/* App Bar - Hidden when Email Studio is active */}
          {!isEmailWorkspaceTab && (
            <AppBar
              position="fixed"
              sx={{
                width: { md: `calc(100% - ${drawerWidth}px)` },
                ml: { md: `${drawerWidth}px` },
                backgroundColor: 'background.paper',
                color: 'text.primary',
                boxShadow: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Toolbar>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2, display: { md: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
                
                <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
                  <Typography variant="body2" color="text.secondary">
                    {activeTab === 'search'
                      ? 'Carrier Search'
                      : activeTab === 'profile'
                        ? 'User Profile'
                      : activeTab === 'history'
                        ? 'Search History'
                        : activeTab === 'activity'
                          ? 'Activity'
                          : 'Admin Portal'}
                  </Typography>
                </Typography>
                {typeof usageRemaining === 'number' && !isEmailWorkspaceTab && (
                  <Chip
                    label={`Remaining: ${usageRemaining}`}
                    color={usageRemaining > 0 ? 'success' : 'warning'}
                    sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
                  />
                )}
              </Toolbar>
            </AppBar>
          )}

        {/* Mobile Hamburger Button - Always visible on mobile */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1200,
            display: { xs: 'flex', md: 'none' },
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Sidebar */}
        <Sidebar
          drawerWidth={drawerWidth}
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchHistory={searchHistory}
          onToggleDarkMode={toggleDarkMode}
          darkMode={darkMode}
          isAdminLoggedIn={isAdminUser}
          isAuthenticatedUser={isAuthenticated}
          user={user}
          logout={() => {
            logout();
            handleLogoutFromApp();
          }}
          usageRemaining={usageRemaining}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: isEmailWorkspaceTab ? 0 : { xs: 1.5, sm: 2, md: 3 },
            width: { md: `calc(100% - ${drawerWidth}px)`, xs: '100%' },
            minHeight: '100vh',
            backgroundColor: 'background.default',
          }}
        >
          {!isEmailWorkspaceTab && <Toolbar />}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
              <Typography variant="h6" color="text.secondary">
                Loading session...
              </Typography>
            </Box>
          )}

          {!isLoading && (
            <>
              {/* SEARCH TAB */}
              {activeTab === 'search' && (
                isAuthenticated ? (
                  <>
                    <SearchDashboard
                      onSearch={handleSearch}
                      onBatchSearch={handleBatchSearch}
                      clearBatchResults={clearBatchResults}
                      onPauseBatchSearch={pauseBatchSearch}
                      onResumeBatchSearch={resumeBatchSearch}
                      loading={loading}
                      error={error}
                      carrierData={carrierData}
                      showBasicInfo={showBasicInfo}
                      onToggleView={toggleInfoView}
                      isBatchPaused={isBatchPaused}
                      isBatchSearching={isBatchSearching}
                      darkMode={darkMode}
                      onQuickEmail={(carrierEmail, carrierName, carrierRecord = null) => {
                        const payload = {
                          email: carrierEmail,
                          name: carrierName,
                          carrier: carrierRecord,
                          timestamp: Date.now(),
                        };
                        setQuickComposePayload(payload);
                        setQuickComposeReturnToSearch(profileSettings.autoReturnToSearchAfterQuickEmail !== false);
                        try {
                          localStorage.setItem('cfp_quick_email', JSON.stringify(payload));
                        } catch (e) { /* ignore */ }
                        // Log activity for quick email compose
                        if (token) {
                          adminApi.logActivity({
                            type: 'quick_email',
                            source: 'carrier_search',
                            metadata: { email: carrierEmail, carrier: carrierName }
                          }, token).catch(() => {});
                        }
                        setActiveTab('email-compose');
                      }}
                    />
                  </>
                ) : (
                  <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      gutterBottom
                    >
                      Sign In Required
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Please log in to run carrier searches and track your daily quota.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setShowLandingPage(true)}
                    >
                      Go to Login
                    </Button>
                  </Box>
                )
              )}

              {/* HISTORY TAB */}
              {activeTab === 'history' && (
            <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                mb: 4
              }}>
                Search History
              </Typography>
              
              {searchHistory.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <History sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary">
                    No search history yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Your search queries will appear here
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {searchHistory.map((search, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 3,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: darkMode ? 'grey.800' : 'grey.50',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.05)'
                        }
                      }}
                      onClick={() => handleSearch(search.term, search.type)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {search.term}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            backgroundColor: 
                              search.type === 'mc' ? 'primary.main' : 
                              search.type === 'dot' ? 'secondary.main' : 'warning.main',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {search.type.toUpperCase()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Searched on {search.timestamp}
                      </Typography>
                      {search.filters && Object.values(search.filters).some(val => val) && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {search.filters.skipBrokers && (
                            <Typography variant="caption" sx={{ 
                              backgroundColor: darkMode ? 'grey.700' : 'grey.200',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              No Brokers
                            </Typography>
                          )}
                          {search.filters.skipInactive && (
                            <Typography variant="caption" sx={{ 
                              backgroundColor: darkMode ? 'grey.700' : 'grey.200',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1
                            }}>
                              Active Only
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
              )}

              {activeTab === 'profile' && (
                isAuthenticated ? (
                  <UserProfile
                    user={user}
                    token={token}
                    darkMode={darkMode}
                    onToggleDarkMode={toggleDarkMode}
                    refreshSession={refreshSession}
                    onSettingsSaved={(nextSettings) => {
                      setProfileSettings(nextSettings);
                    }}
                  />
                ) : (
                  <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
                    <AccountCircle sx={{ fontSize: 72, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                      Profile requires login
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Please log in to view your subscription details and personal app settings.
                    </Typography>
                    <Button variant="contained" size="large" onClick={() => setShowLandingPage(true)}>
                      Go to Login
                    </Button>
                  </Box>
                )
              )}

          {activeTab === 'activity' && (
            <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(16,185,129,0.06) 100%)',
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                        User Activity
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Real-time usage summary across search, outreach, and inbox.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip label="Auto-saved" color="success" size="small" />
                      <Chip label="Audit safe" variant="outlined" size="small" />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                {[
                  { label: 'Searches Today', value: searchesToday, icon: <History />, color: '#2563eb' },
                  { label: 'Searches This Month', value: searchesMonth, icon: <History />, color: '#1d4ed8' },
                  { label: 'Calls Today', value: callsToday, icon: <History />, color: '#0ea5e9' },
                  { label: 'Calls This Month', value: callsMonth, icon: <History />, color: '#0284c7' },
                  { label: 'Emails Sent (Today)', value: emailSentToday, icon: <MailOutline />, color: '#10b981' },
                  { label: 'Emails Sent (Month)', value: emailSentMonth, icon: <MailOutline />, color: '#059669' },
                  { label: 'Inbox Replies (Today)', value: inboxRepliesToday, icon: <Forum />, color: '#f59e0b' },
                  { label: 'Inbox Replies (Month)', value: inboxRepliesMonth, icon: <Forum />, color: '#d97706' },
                  { label: 'Hours Worked (Today)', value: formatHours(hoursWorkedToday), icon: <AccessTime />, color: '#8b5cf6' },
                  { label: 'Hours Worked (Month)', value: formatHours(hoursWorkedMonth), icon: <AccessTime />, color: '#7c3aed' },
                ].map((card, idx) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                    <Card
                      sx={{
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              backgroundColor: `${card.color}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: card.color,
                            }}
                          >
                            {card.icon}
                          </Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {card.label}
                          </Typography>
                        </Stack>
                        <Typography variant="h4" fontWeight={800}>
                          {card.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Card
                sx={{
                  mt: 3,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Activity Timeline
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Detailed logs will appear here once backend timelines are enabled. Entries are immutable to preserve compliance.
                      </Typography>
                    </Box>
                    <Chip label="Coming soon" variant="outlined" />
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}

          {isAuthenticated && emailWorkspaceMounted && (
            <Box sx={{ height: '100%', width: '100%', overflow: 'hidden', display: isEmailWorkspaceTab ? 'block' : 'none' }}>
              <EmailWorkspace
                carrierData={carrierData}
                inboxOnly={isInboxOnlyView}
                quickComposePayload={quickComposePayload}
                onQuickComposeConsumed={() => setQuickComposePayload(null)}
                quickComposeReturnToSearch={quickComposeReturnToSearch}
                onQuickComposeSent={() => {
                  setQuickComposeReturnToSearch(false);
                  setActiveTab('search');
                }}
                currentView={
                  activeTab === 'email-compose' ? 0 :
                  activeTab === 'email-campaign' ? 1 :
                  activeTab === 'email-templates' ? 2 :
                  activeTab === 'email-inbox' ? 3 :
                  activeTab === 'email-seen' ? 4 :
                  activeTab === 'email-accounts' ? 5 :
                  0
                }
              />
            </Box>
          )}

          {!isAuthenticated && isEmailWorkspaceTab && (
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
              >
                {isInboxOnlyView ? 'Email inbox requires login' : 'Email workspace requires login'}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {isInboxOnlyView
                  ? 'Sign in to view your unified email inbox.'
                  : 'Connect an account and send messages once you are signed in.'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowLandingPage(true)}
              >
                Go to Login
              </Button>
            </Box>
          )}

              {/* ADMIN PORTAL TAB */}
              {activeTab === 'admin' && isAdminUser && (
                <AdminPortal />
              )}
            </>
          )}
        </Box>
      </Box>
      )}

      {/* Limit Reached Dialog */}
      <LimitReachedDialog
        open={limitDialogOpen}
        onClose={() => setLimitDialogOpen(false)}
        limitType={limitType}
        limitData={limitData}
      />

      <Dialog
        open={landingBatchLimitOpen}
        onClose={() => setLandingBatchLimitOpen(false)}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              mb: 2,
              display: 'inline-flex',
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              fontSize: '0.78rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'warning.main',
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.22)',
            }}
          >
            Batch Search Limit
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.04em', mb: 1.2 }}>
            Need more than 100 searches?
          </Typography>
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Landing page batch search without login is limited to 100 MC searches. To search more carriers, purchase a subscription or log in to continue with extended access.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, pt: 0, justifyContent: 'space-between' }}>
          <Button onClick={() => setLandingBatchLimitOpen(false)} color="inherit">
            Maybe Later
          </Button>
          <Stack direction="row" spacing={1.2}>
            <Button
              variant="outlined"
              onClick={() => {
                setLandingBatchLimitOpen(false);
                setShowLandingPage(true);
              }}
            >
              Log In
            </Button>
            <Button
              variant="contained"
              onClick={() => setLandingBatchLimitOpen(false)}
              sx={{
                background: 'linear-gradient(135deg, #2563eb 0%, #10b981 100%)',
              }}
            >
              Purchase Subscription
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;
